'use client'; // Error components must be Client Components
import { CapsicumError, useIntl, usePage, useSetPageType } from '@capsicum/common';
import { useCallback } from 'react';
import styled from 'styled-components';

import { useRouter } from '../../../hooks/router/useCustomRouter';
import { Button } from '../../atoms/buttons/button/Button';
import { Goat } from '../../molecules/goat/Goat';

export function GenericError({ error, errorId, reset }: { error: Error; errorId?: string; reset: () => void }) {
	const { $t } = useIntl();
	const { back } = useRouter();
	const isCapsicumError = error instanceof CapsicumError;
	const title = $t(isCapsicumError ? error.errorTitle : 'error.default');
	const buttonText = $t(isCapsicumError && error.actionMessage ? error.actionMessage : 'global.button.back');
	const goatImage = isCapsicumError ? error.goatImage : 'error';
	const errorMessage =
		isCapsicumError && error.errorDescription
			? $t(error.errorDescription, error.errorDescriptionValues)
			: $t('error.defaultDescription');

	const onClick = useCallback(() => {
		if (isCapsicumError) {
			return error.actionCallback ? error.actionCallback(reset) : reset();
		}
		back();
	}, [error, reset, isCapsicumError, back]);

	const hideButton = isCapsicumError ? error.hideButton ?? false : false;

	const { pageType } = usePage();

	useSetPageType(pageType === 'page-with-sidePanel' ? 'page' : pageType);

	return (
		<S.Wrapper>
			<Goat image={goatImage} testID="error" title={title}>
				{errorMessage && (
					<S.ErrorMessage>
						{errorMessage}
						{errorId && (
							<small>
								<br />
								{`ID: ${errorId}`}
							</small>
						)}
					</S.ErrorMessage>
				)}
				{hideButton === false && (
					<S.ButtonContainer>
						<Button testID="try-again" text={buttonText} onClick={() => onClick()} />
					</S.ButtonContainer>
				)}
			</Goat>
		</S.Wrapper>
	);
}

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: ${({ theme }) => theme.spacings.large} 0;
		height: 100%;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.screen};
	`;
	export const ErrorMessage = styled.p`
		text-align: center;
		${({ theme }) => theme.fonts.text.text1};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;
	export const ButtonContainer = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: center;
	`;
}
